import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { noop } from 'lodash';
import { useBoolean } from 'usehooks-ts';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { Button, Popover } from '@ebsco-ui/ebsco-ui';

import { FeatureFlagDisplay, Icon, LinkMenuItem } from '@app/components';
import { useFeatureFlagsContext } from '@app/contexts';
import { getMatchedFeatureFlag } from '@app/utils';
import {
  NavigationDropdownItem,
  navigationDropdownMessages,
} from '@app/translations';
import { FeatureFlagToMatch, SetState } from '@app/constants';

import css from '../NavigationPanel.module.scss';

interface NavigationLink {
  href: string;
  label: string;
  flag?: FeatureFlagToMatch;
}

export interface NavigationDropdownProps {
  name: NavigationDropdownItem;
  links: NavigationLink[];
  icon: IconDefinition;
  isMobile?: boolean;
  setShowDrawer?: SetState<boolean>;
}

export const NavigationDropdown: FC<NavigationDropdownProps> = ({
  name,
  links,
  icon,
  isMobile,
  setShowDrawer,
}) => {
  const location = useLocation();
  const { $t } = useIntl();
  const featureFlags = useFeatureFlagsContext();
  const { value: isDropdownExpanded, toggle: toggleDropdown } =
    useBoolean(false);

  const shouldRender = links.some(
    link =>
      !link.flag ||
      getMatchedFeatureFlag({
        flags: featureFlags,
        flagToMatch: link.flag,
      }).isActive
  );

  if (!shouldRender) {
    return null;
  }

  const handleCloseMobileMenu = () => setShowDrawer && setShowDrawer(false);
  const getDropdownLinksLabel = ({ name, isExpanded }) =>
    $t(navigationDropdownMessages[name][isExpanded ? 'collapse' : 'expand']);

  const getDropdownLinks = (
    links: NavigationLink[] = [],
    handlePopoverClose = noop
  ) => (
    <ul className={css.dropdownLinks}>
      {links.map(({ href, label, flag }) => (
        <FeatureFlagDisplay key={label} flagToMatch={flag}>
          <LinkMenuItem
            to={href}
            activeClassName={css.isActive}
            onClick={() => {
              handleCloseMobileMenu();
              handlePopoverClose();
            }}
          >
            {label}
          </LinkMenuItem>
        </FeatureFlagDisplay>
      ))}
    </ul>
  );

  const isActive = links.some(({ href }) => location.pathname.startsWith(href));

  const buttonContent = (
    <>
      <Icon icon={icon} className={css.icon} />
      <span className={css.content}>
        {$t(navigationDropdownMessages[name].label)}
      </span>
      <Icon
        icon={faChevronRight}
        className={cn(css.arrow, {
          [css.isRotate]: isMobile && isDropdownExpanded,
        })}
      />
    </>
  );

  return (
    <li>
      {isMobile ? (
        <>
          <Button
            className={cn(css.navigationLinkDropdown, css.link, {
              [css.isActive]: isActive,
            })}
            styleType="flat-icon"
            aria-label={getDropdownLinksLabel({
              name,
              isExpanded: isDropdownExpanded,
            })}
            onClick={toggleDropdown}
          >
            {buttonContent}
          </Button>
          {isDropdownExpanded && getDropdownLinks(links)}
        </>
      ) : (
        <Popover
          uniqueName={`${name}-links`}
          panelPlacement="bottom-start"
          className={css.wrapper}
        >
          <Popover.ContextConsumer>
            {({ isOpen, closePopover }) => (
              <>
                <Popover.Button
                  id={`${name}-links-button`}
                  styleType="flat-icon"
                  className={cn(
                    css.navigationLinkDropdown,
                    css.link,
                    { [css.isActive]: isActive },
                    'popoverButton'
                  )}
                  aria-label={getDropdownLinksLabel({
                    name,
                    isExpanded: isOpen,
                  })}
                >
                  <div className={css.label}>{buttonContent}</div>
                </Popover.Button>
                <Popover.Panel
                  aria-describedby={`${name}-links-button`}
                  className="popoverWrapper"
                >
                  {getDropdownLinks(links, closePopover)}
                </Popover.Panel>
              </>
            )}
          </Popover.ContextConsumer>
        </Popover>
      )}
    </li>
  );
};
